import React, {
  FormEvent,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import Container from "./Container";
import DateRangeInput from "./DateRangeInput";
import ClassDropdown from "./ClassDropdown";
import StudentDropdown from "./StudentDropdown";
import HashtagInput from "./HashtagInput";
import IsFavoriteCheckbox from "./IsFavoriteCheckbox";
import PrimaryActionButton from "./PrimaryActionButton";
import SearchIcon from "assets/icons/SearchIcon";
import ClearIcon from "assets/icons/ClearIcon";
import { searchHashTag } from "services/pictureServices";
import { PictureStudentOption } from "config/constants";

interface SearchParams {
  startDate?: string;
  endDate?: string;
  classId?: number;
  studentId?: number | PictureStudentOption | null;
  hashtagId?: number;
  isFavorite?: boolean;
}

interface PictureSearchInputProps {
  onSearch: () => Promise<void>;
  onSearchChange: (searchParams: SearchParams) => void;
  initialValues?: SearchParams;
}

const TODAY = new Date();
TODAY.setHours(23, 59, 59, 999);

// Helper function to format date as YYYY-MM-DD
const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to parse YYYY-MM-DD string to Date
const parseDateFromYMD = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const PictureSearchInput: React.FC<PictureSearchInputProps> = ({
  onSearch,
  onSearchChange,
  initialValues = {},
}) => {
  const { t } = useTranslation();

  // Local state for search parameters - now storing dates as YYYY-MM-DD strings
  const [startDate, setStartDate] = useState<string>(
    initialValues.startDate || formatDateToYMD(new Date())
  );
  const [endDate, setEndDate] = useState<string>(
    initialValues.endDate || formatDateToYMD(new Date())
  );
  const [classId, setClassId] = useState<number | null>(
    initialValues.classId || null
  );
  const [studentId, setStudentId] = useState<
    number | PictureStudentOption | null
  >(initialValues.studentId || null);
  const [hashtagId, setHashtagId] = useState<number | null>(
    initialValues.hashtagId || null
  );
  const [isFavorite, setIsFavorite] = useState<boolean>(
    initialValues.isFavorite || false
  );
  const [hashTagInputValue, setHashTagInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Memoize search parameters to prevent unnecessary re-renders and emissions
  const searchParams = useMemo(
    () => ({
      startDate,
      endDate,
      classId: classId || undefined,
      studentId: studentId || undefined,
      hashtagId: hashtagId || undefined,
      isFavorite,
    }),
    [startDate, endDate, classId, studentId, hashtagId, isFavorite]
  );

  // Memoize the onSearchChange callback to prevent unnecessary calls
  const memoizedOnSearchChange = useCallback(onSearchChange, [onSearchChange]);

  // Update parent component when search parameters change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      memoizedOnSearchChange(searchParams);
    }, 100); // 100ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchParams, memoizedOnSearchChange]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Handle hashtag search if needed
    if (hashTagInputValue.length > 0 && !hashtagId) {
      const hashtags = await searchHashTag(hashTagInputValue);
      const foundHashtag = hashtags.find(
        (tag) => tag.name === hashTagInputValue
      );
      if (foundHashtag) {
        setHashtagId(foundHashtag.id);
      }
    }

    await onSearch();
    setIsLoading(false);
  };

  const handleDateChange = (dates: Date[]) => {
    console.log("Selected dates:", dates);
    setStartDate(formatDateToYMD(dates[0]));
    setEndDate(formatDateToYMD(dates[1]));
  };

  const handleClassChange = (id: number | null) => {
    setClassId(id);
    setStudentId(null); // Reset student when class changes
  };

  const handleStudentChange = (id: number | string | null) => {
    if (id === PictureStudentOption.all || id === PictureStudentOption.class) {
      setStudentId(id);
    } else {
      setStudentId(id ? Number(id) : null);
    }
  };

  const handleHashtagChange = (value: number | null) => {
    setHashtagId(value);
  };

  const handleFavoriteChange = (status: boolean) => {
    setIsFavorite(status);
  };

  const handleClear = () => {
    const today = formatDateToYMD(new Date());
    setStartDate(today);
    setEndDate(today);
    setClassId(null);
    setStudentId(null);
    setHashtagId(null);
    setIsFavorite(false);
    setHashTagInputValue("");
  };

  return (
    <Container>
      <form
        className="lg:flex lg:items-stretch space-x-0 lg:space-x-2 space-y-2 lg:space-y-0"
        onSubmit={handleSubmit}
      >
        <div className="w-full lg:w-[30%]">
          <DateRangeInput
            name={""}
            currentStartDate={parseDateFromYMD(startDate)}
            currentEndDate={parseDateFromYMD(endDate)}
            maxDate={TODAY}
            onChange={handleDateChange}
          />
        </div>
        <div className="w-full lg:w-[15%]">
          <ClassDropdown
            currentClassId={classId?.toString() || ""}
            onChange={handleClassChange}
          />
        </div>
        <div className="w-full lg:w-[20%]">
          <StudentDropdown
            currentClassId={classId || 0}
            currentStudentId={studentId ? studentId.toString() : ""}
            onChange={handleStudentChange}
          />
        </div>
        <div className="w-full lg:w-[15%]">
          <HashtagInput
            inputValue={hashTagInputValue}
            onInputChange={(value) => setHashTagInputValue(value)}
            value={hashtagId}
            onChange={handleHashtagChange}
          />
        </div>
        <div className="w-full lg:w-[10%]">
          <IsFavoriteCheckbox
            isFavorite={isFavorite}
            onChange={handleFavoriteChange}
          />
        </div>
        <div className="w-full lg:w-[10%] flex items-center">
          <PrimaryActionButton
            isButton={false}
            label={
              <div className="w-fit m-auto flex items-center space-x-1">
                <SearchIcon width={24} height={24} color={"white"} />
                <p>{t("_published_picture._search")}</p>
              </div>
            }
            disabled={!classId || !studentId}
            loading={isLoading}
          />
        </div>
      </form>
      <div className="flex justify-end">
        <div className="w-full lg:w-[10%]">
          <button
            type="button"
            className="w-fit text-primary underline flex justify-center items-center space-x-1"
            onClick={handleClear}
          >
            <ClearIcon width={24} height={24} />
            <p className="font-bold">{t("_published_picture._clear")}</p>
          </button>
        </div>
      </div>
    </Container>
  );
};

export default PictureSearchInput;
